// import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
// import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Reset from 'assets/images/Repertoire/Reset.svg';
import TempoIconLight from 'assets/images/Repertoire/TempoIconLight.svg';
import TempoIconLightAta from 'assets/images/Repertoire/TempoIconLightAta.svg';
import { Box, Slider, TextField, Typography, useMediaQuery } from '@mui/material';
import { RangeSlider } from './RangeSlider';
import { useDispatch, useSelector } from 'react-redux';
import { useHoldToRepeat } from 'Hooks/useHoldToRepeat';
import { NotifyPhaserEvents } from 'Phaser/EventBus2';
import { MainAppReducer } from 'Types';
import * as appActions from 'Actions/app';
import TempoPlus from 'assets/images/Repertoire/TempoPlus.png';
import TempoMinus from 'assets/images/Repertoire/TempoMinus.png';
import { useTempoContext } from 'Contexts/TempoContext';
import { InfoOutline, InfoOutlineRounded } from '@mui/icons-material';
import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { CustomSwitch, CustomSwitchOutline } from './StyledComponents';
import { useFeatureFlag, FeatureFlag, isFeatureEnabled } from 'Utils/FeatureFlags';
import { sliderToTempoPercentage } from 'Utils/tempoSliderMapping';
import { PhaserToReactListeners, RemoveListener } from 'Phaser/EventBus2';
import { AppTourStepHelper } from 'Phaser/EventBus2/ToReact';
import { APP_TOUR_CHROME_ATTR, APP_TOUR_POPPER_CLASS } from 'Constants/AppTourMarkup';
import { Minus, Plus } from 'lucide-react';
import { defaultAtaSettings } from 'Reducers/mainAppReducer';

const CustomThresholdSetting = ({
  label,
  details,
  value: initValue,
  onChange,
  min,
  max,
  prefix,
  isPct,
}: {
  label: string;
  details: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
  prefix?: string;
  isPct?: boolean;
}) => {
  const [value, setValue] = useState(initValue);

  const { incrementHandlers, decrementHandlers } = useHoldToRepeat(
    value,
    //mapTempoToSlider, // Called on release (and during hold via onLocalChange)
    (finalValue) => {
      onChange(finalValue);
    },
    {
      min,
      max,
      onLocalChange: setValue,
    },
  );

  useEffect(() => {
    setValue(initValue);
  }, [initValue]);

  const buttonStyles = {
    backgroundColor: '#1A212E',
    border: '0.5px solid #CA7C62',
    borderRadius: 2,
    width: '35px',
    height: '35px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      cursor: 'pointer',
      boxShadow: '0px 0px 5px 5px rgb(202,124,98,.5)',
    },
  };
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: 'row',
          marginBottom: '10px',
          marginTop: '10px',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'start', flexDirection: 'column' }}>
          <Typography sx={{ textAlign: 'left', fontFamily: 'Lato', fontSize: '14px' }}>
            {label}
          </Typography>
          <Typography
            sx={{ textAlign: 'left', fontFamily: 'Lato', fontSize: '12px', color: '#6C6C88' }}
          >
            {details}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            flexDirection: 'row',
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Box sx={{ ...buttonStyles, borderColor: '#433B43' }} {...decrementHandlers}>
            <Minus color="#CA7C62" size={20}></Minus>
          </Box>
          <Typography
            sx={{
              justifyContent: 'space-around',
              display: 'flex',
              flexDirection: 'row',
              fontFamily: 'Lato',
              fontSize: '20px',
              width: '60px',
              height: '35px',
              alignSelf: 'center',
            }}
          >
            {`${prefix ?? ''}${value}${isPct ? '%' : ''}`}
            {!isPct && (
              <Typography sx={{ alignSelf: 'center', marginLeft: '5px', fontSize: '10px' }}>
                BPM
              </Typography>
            )}
          </Typography>
          <Box sx={{ ...buttonStyles, borderColor: '#2C3965' }} {...incrementHandlers}>
            <Plus color="#6F87F1" size={16}></Plus>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const CustomSettings = () => {
  const tempoContext = useTempoContext();

  const {
    incThreshold,
    setIncThreshold,
    setDecAmt,
    setDecThreshold,
    setIncAmt,
    decThreshold,
    incAmount,
    decAmount,
  } = tempoContext.customAtaThreshold;

  const reset = useCallback(() => {
    const defaults = defaultAtaSettings;
    setIncThreshold(defaults.customIncThreshold);
    setDecThreshold(defaults.customDecThreshold);
    setIncAmt(defaults.customIncAmt);
    setDecAmt(defaults.customDecAmt);
  }, [setIncThreshold, setDecThreshold, setIncAmt, setDecAmt]);

  // Forward custom thresholds to the Phaser AdaptiveTempoController. Depend on the Redux settings
  // (the committed source of truth) rather than the per-input local state so changes are coalesced
  // into a single event instead of one per control.
  const ataSettings = useSelector(
    (state: MainAppReducer) => state.mainAppReducer.ataSettings ?? defaultAtaSettings,
  );
  const { customIncThreshold, customDecThreshold, customIncAmt, customDecAmt } = ataSettings;

  useEffect(() => {
    NotifyPhaserEvents.UpdateAtaConfg({
      config: {
        incThreshold: customIncThreshold,
        decThreshold: customDecThreshold,
        incAmt: customIncAmt,
        decAmt: customDecAmt,
      },
    });
  }, [customIncThreshold, customDecThreshold, customIncAmt, customDecAmt]);

  console.debug(
    '[CustomSettings] incThreshold',
    incThreshold,
    'decThreshold',
    decThreshold,
    'incAmount',
    incAmount,
    'decAmount',
    decAmount,
  );
  console.debug('[CustomSettings] ', tempoContext.customAtaThreshold);

  return (
    <Box
      sx={{
        backgroundColor: '#0F172A80',
        padding: '10px',
        border: '1px solid #6F87F133',
        borderRadius: '10px',
      }}
    >
      <CustomThresholdSetting
        label="Increase Tempo Threshold"
        details="Raise tempo when accuracy is above"
        value={incThreshold}
        min={80}
        max={100}
        onChange={setIncThreshold}
        isPct={true}
      />
      <CustomThresholdSetting
        label="Decrease Tempo Threshold"
        details="Lower tempo when accuracy is below"
        value={decThreshold}
        min={60}
        max={100}
        onChange={setDecThreshold}
        isPct={true}
      />
      <CustomThresholdSetting
        label="Increase Tempo Amount"
        details="How much to speed up"
        value={incAmount}
        min={0}
        max={20}
        onChange={setIncAmt}
        prefix="+"
      />
      <CustomThresholdSetting
        label="Decrease Tempo Amount"
        details="How much to slow down"
        value={decAmount}
        min={0}
        max={20}
        onChange={setDecAmt}
        prefix="-"
      />
      <Box>
        <img src={Reset} width={32} onClick={reset} style={{ cursor: 'pointer' }} />
        <Typography sx={{ fontFamily: 'Lato' }}>Reset</Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
          fontSize: '12px',
          justifyContent: 'center',
          color: 'gray',
          marginTop: '5px',
        }}
      >
        <InfoOutlineRounded />
        Tap + or – to adjust. Hold to change faster.
      </Box>
    </Box>
  );
};

/** valueA = current tempo %, valueB = goal tempo % (identity-based; handles can cross) */
type StyledSliderRangeProps = {
  variant: 'range';
  /** Current tempo as percentage of performance (handle A) */
  sliderValueTempo: number;
  /** Goal tempo as percentage of performance (handle B) */
  sliderValueGoal: number;
  onRangeChange: (valueA: number, valueB: number) => void;
  min?: number;
  max?: number;
  /** Tooltip for the left handle (e.g. goal tempo) */
  tooltipLeft?: React.ReactNode;
  /** Tooltip for the right handle (e.g. current tempo) */
  tooltipRight?: React.ReactNode;
  /** Persistent label for the left handle (moves with handle) */
  labelLeft?: React.ReactNode;
  /** Persistent label for the right handle (moves with handle) */
  labelRight?: React.ReactNode;
  /** When true, left handle: label above and tooltip below (default: label below, tooltip on top). */
  flipLabelPositionLeft?: boolean;
  /** When true, right handle: label above and tooltip below (default: label below, tooltip on top). */
  flipLabelPositionRight?: boolean;
};

/** Single-handle mode: one value, normal slider behavior. */
type StyledSliderSingleProps = {
  variant: 'single';
  /** Current value (percentage or raw) */
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  /** Optional label below the slider (defaults to valueLabelFormat(value)) */
  label?: React.ReactNode;
  /** Value label in thumb bubble and optional label below (e.g. "50%") */
  valueLabelFormat?: (value: number) => string;
};

export type StyledSliderVariantProps = StyledSliderRangeProps | StyledSliderSingleProps;

const sharedSliderStyles = {
  color: '#6F87F1',
  '& .MuiSlider-track': {
    color: '#2D3A67',
  },
  '& .MuiSlider-rail': {
    color: '#2D3A67',
  },
};

/**
 * Unified slider wrapper: use variant="single" for one handle (normal slider) or
 * variant="range" for two handles (current tempo / goal tempo). Shared styling for both.
 */
const StyledSliderVariant: React.FC<StyledSliderVariantProps> = (props) => {
  if (props.variant === 'single') {
    const {
      value,
      onChange,
      min = 0,
      max = 100,
      label,
      valueLabelFormat = (v) => `${Math.round(v)}%`,
    } = props;
    return (
      <Box sx={{ textAlign: 'center', margin: '20px 30px 10px 30px' }}>
        <Slider
          sx={sharedSliderStyles}
          value={value}
          onChange={(_e, val) => onChange(Array.isArray(val) ? val[0] : val)}
          min={min}
          max={max}
          valueLabelDisplay="auto"
          valueLabelFormat={valueLabelFormat}
          aria-labelledby="styled-slider-single"
        />
        {label != null ? (
          <Typography sx={{ mt: 0.5 }}>{label}</Typography>
        ) : (
          <Typography>{valueLabelFormat(value)}</Typography>
        )}
      </Box>
    );
  }

  const {
    sliderValueTempo,
    sliderValueGoal,
    onRangeChange,
    min = 0,
    max = 100,
    tooltipLeft,
    tooltipRight,
    labelLeft,
    labelRight,
    flipLabelPositionLeft,
    flipLabelPositionRight,
  } = props;

  return (
    <Box sx={{ textAlign: 'center', margin: '20px 30px 30px 10px' }}>
      <RangeSlider
        value={[sliderValueTempo, sliderValueGoal]}
        onChange={([left, right]) => onRangeChange(left, right)}
        min={min}
        max={max}
        ariaLabelLeft="Minimum tempo (percentage)"
        ariaLabelRight="Maximum tempo (percentage)"
        ariaLabelGroup="Tempo range"
        ariaValueTextLeft={(v) => `${Math.round(v)}%`}
        ariaValueTextRight={(v) => `${Math.round(v)}%`}
        thumbLeftClassName="tempo-range-thumb-left"
        thumbRightClassName="tempo-range-thumb-right"
        thumbLeftColor="#6F87F1"
        thumbRightColor="#469F6A"
        tooltipLeft={tooltipLeft}
        tooltipRight={tooltipRight}
        labelLeft={labelLeft}
        labelRight={labelRight}
        flipLabelPositionLeft={flipLabelPositionLeft}
        flipLabelPositionRight={flipLabelPositionRight}
        style={{
          track: { backgroundColor: '#2D3A67' },
          rail: { backgroundColor: '#2D3A67' },
        }}
      />
    </Box>
  );
};

const controlsImagesStyles = {
  marginTop: '-1px',
  maxHeight: '54px',
};

type TempoControlsProps = {
  tempoToggleRef: React.RefObject<HTMLDivElement | null>;
  tempoContainerRef: React.RefObject<HTMLImageElement | null>;
  tempoInputRef: React.RefObject<HTMLInputElement | null>;
  playing: boolean;
  compact?: boolean;
  styleOverride?: React.CSSProperties;
  isRepertoire?: boolean;
};

const TempoControls = ({
  tempoToggleRef,
  tempoContainerRef,
  tempoInputRef,
  playing,
  compact = false,
  styleOverride,
  isRepertoire,
}: TempoControlsProps) => {
  const {
    //isExpanded,
    //setIsExpanded,
    tempo,
    performanceTempo,
    ataEnabled,
    changeTempo,
    freePlayMode,
  } = useTempoContext();

  const [inputBoxText, setInputBoxText] = useState(tempo.toString());

  useEffect(() => {
    setInputBoxText(Math.round(tempo).toString());
  }, [tempo]);

  const [isExpanded, setIsExpanded] = useState(false);

  const commitTempoChange = useCallback(
    (text: string) => {
      changeTempo({ value: Math.round(parseInt(text, 10)), goal: false });
      /*console.debug('[change] commitTempoChange', text);
      const value = Math.max(Math.min(parseInt(text, 10), max_tempo_value), min_tempo_value);
      mapTempoToSlider(value, false);
      setInputBoxText(value.toString());*/
    },
    //[max_tempo_value, min_tempo_value, mapTempoToSlider],
    [changeTempo],
  );

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      commitTempoChange(inputBoxText);
      const el = e.target instanceof Element ? e.target : null;
      // App tour content is in a portaled MUI Popper — not inside tempo refs; ignore those clicks
      // so mousedown doesn't collapse the panel before "Next" runs (invalid anchor / extra click).
      if (el?.closest(`.${APP_TOUR_POPPER_CLASS}`) || el?.closest(`[${APP_TOUR_CHROME_ATTR}]`)) {
        return;
      }
      const target = e.target as Node;
      const isInsideContainer = tempoContainerRef.current?.contains(target);
      const isInsideToggle = tempoToggleRef.current?.contains(target);
      if (!isInsideContainer && !isInsideToggle) {
        // Commit any pending manual tempo input before closing (input blur won't fire once we unmount)
        //setManualTempoChange();
        setIsExpanded(false);
      }
    },
    [tempoContainerRef, tempoToggleRef, setIsExpanded, inputBoxText, commitTempoChange],
  );

  useEffect(() => {
    if (isExpanded) {
      // Use pointerdown, not mousedown: iOS Safari only synthesizes mouse events
      // for "clickable" elements, so a tap on the score canvas never fires
      // mousedown and the panel would never close. pointerdown fires for any tap.
      document.addEventListener('pointerdown', handleClickOutside, true);
      return () => document.removeEventListener('pointerdown', handleClickOutside, true);
    }
  }, [isExpanded, handleClickOutside]);

  useEffect(() => {
    const handleAppTourHelper = ({ step }: { step: AppTourStepHelper }) => {
      if (step === AppTourStepHelper.ExpandTempoControls) {
        setIsExpanded(true);
      } else if (step === AppTourStepHelper.EndTour) {
        setIsExpanded(false);
      }
    };
    PhaserToReactListeners.AppTourHelper(handleAppTourHelper);
    return () => {
      RemoveListener('AppTourHelper', handleAppTourHelper);
    };
  }, [setIsExpanded]);

  const shrunk = playing || compact;

  /*if (useFeatureFlag(FeatureFlag.USE_ADAPTIVE_TEMPO)) {*/
  return (
    <Box
      id="tempo-controls-container"
      sx={{ position: 'relative', transition: 'top 1s ease-in-out', ...styleOverride }}
      className={playing ? 'playing' : ''}
    >
      {isExpanded && (
        <TempoContainer
          playing={playing}
          tempoContainerRef={tempoContainerRef}
          tempoInputRef={tempoInputRef}
          tempoToggleRef={tempoToggleRef}
          inputBoxText={inputBoxText}
          setInputBoxText={setInputBoxText}
          commitTempoChange={commitTempoChange}
          isRepertoire={isRepertoire}
        />
      )}
      <Box
        id={'tempo'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: shrunk ? '80px' : '210px',
          backgroundColor: '#243056',
          borderRadius: '33px',
          padding: '3px',
          marginRight: '15px',
          flex: shrunk ? 'none' : 1,
          transition: '0.3s ease-in-out',
          height: shrunk ? '30px' : '50px',
          '&:hover': {
            cursor: 'pointer',
            transition: '0.3s',
            borderRadius: '33px',
            boxShadow: '0px 0px 5px 5px rgb(222,144,118,.5)',
          },
        }}
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
        ref={tempoToggleRef}
      >
        <img
          style={{
            ...controlsImagesStyles,
            marginLeft: shrunk ? '-10px' : '1px',
            /*minHeight: playing ? '40px' : '56px',*/
            marginTop: '1px',
            height: shrunk ? '40px' : '56px',
            transition: 'height 0.3s ease-in-out',
          }}
          src={
            ataEnabled && isFeatureEnabled(FeatureFlag.USE_ADAPTIVE_TEMPO)
              ? TempoIconLightAta
              : TempoIconLight
          }
        />
        <Box
          sx={{
            marginLeft: shrunk ? '0px' : '10px',
            display: 'flex',
            alignItems: 'center',
            color: '#ECECED',
            flex: 1,
          }}
        >
          <Typography
            sx={{ ...detailsTitleTextStyles, flex: 1, fontSize: '16px', fontWeight: 700 }}
          >
            {Math.round(tempo)} {shrunk ? '' : 'BPM'}
          </Typography>
          {!shrunk &&
            !freePlayMode &&
            (tempo > performanceTempo ? (
              <Typography style={{ marginLeft: '8px', marginRight: '5px' }}> 🔥 </Typography>
            ) : (
              <Box
                style={{
                  marginLeft: '8px',
                  marginRight: '10px',
                  width: '15px',
                  height: '15px',
                  borderRadius: '50%',
                  backgroundColor: tempo >= (performanceTempo || 0) ? '#40E16D' : '#EB3459',
                }}
              />
            ))}
          {!shrunk && (
            <KeyboardArrowDownIcon style={{ stroke: 'white', width: 28, strokeWidth: 0.8 }} />
          )}
        </Box>
      </Box>
    </Box>
  );
  /*} else {
    return <TempoContainerLarge isSmallScreen={false} freePlay={false} />;
  }*/
};

type TempoContainerProps = TempoControlsProps & {
  tempoContainerRef: React.RefObject<HTMLImageElement | null>;
  inputBoxText: string;
  setInputBoxText: (text: string) => void;
  commitTempoChange: (text: string) => void;
};

const TempoContainer = ({
  tempoContainerRef,
  tempoToggleRef,
  tempoInputRef,
  inputBoxText,
  setInputBoxText,
  commitTempoChange,
  isRepertoire,
}: TempoContainerProps) => {
  const {
    tempo,
    performanceTempo,
    sliderValueTempo,
    sliderValueGoal,
    max_tempo_value,
    min_tempo_value,
    goalTempo,
    usingCustomGoalTempo,
    freePlayMode,
    resetTemp,
    changeTempo,
    ataEnabled,
  } = useTempoContext();

  const handleInputBoxBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      console.debug('[change] handleInputBoxBlur', e.target.value);
      commitTempoChange(e.target.value);
    },
    [commitTempoChange],
  );

  const leftPos = 8; // getPosition().x

  const isMobile = useMediaQuery('(max-width:960px)');

  // On mobile (≤960px) the toggle can sit near either edge — the left in
  // RepertoirePlay, the right in Lesson — so a hardcoded offset can't anchor the
  // popup to the pill in both. Measure the toggle and position the popup under
  // it, clamped to the viewport so it never runs off-screen. The carot is then
  // placed to keep pointing at the toggle even after clamping shifts the popup.
  const MOBILE_POPUP_WIDTH = 370;
  const VIEWPORT_MARGIN = 8;
  const CARET_HALF = 16;
  const [mobileAnchor, setMobileAnchor] = useState<{
    left: number;
    top: number;
    caretLeft: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (!isMobile) {
      setMobileAnchor(null);
      return;
    }
    const compute = () => {
      const toggle = tempoToggleRef.current;
      if (!toggle) return;
      const rect = toggle.getBoundingClientRect();
      const vw = window.innerWidth;
      const toggleCenter = rect.left + rect.width / 2;
      let left = toggleCenter - MOBILE_POPUP_WIDTH / 2;
      left = Math.min(left, vw - MOBILE_POPUP_WIDTH - VIEWPORT_MARGIN);
      left = Math.max(left, VIEWPORT_MARGIN);
      const caretLeft = Math.min(
        Math.max(toggleCenter - left - CARET_HALF, CARET_HALF),
        MOBILE_POPUP_WIDTH - 3 * CARET_HALF,
      );
      setMobileAnchor({ left, top: rect.bottom + 10, caretLeft });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [isMobile, tempoToggleRef]);

  const modalPosition = isMobile
    ? {
        position: 'fixed' as const,
        left: `${mobileAnchor?.left ?? VIEWPORT_MARGIN}px`,
        top: `${mobileAnchor?.top ?? 84}px`,
      }
    : { left: `${leftPos - 32}px`, top: 'calc(100% + 10px)' };

  // Press-and-hold behavior for tempo +/- buttons
  // Note: For RepertoirePlay, updates happen on each tick via mapTempoToSlider
  // (triggers tempo state chain → updateRepertoireDataLocal which is just local Redux state)
  // The main batching benefit is in Lesson.tsx which directly dispatches setTempo
  const { incrementHandlers, decrementHandlers } = useHoldToRepeat(
    tempo,
    //mapTempoToSlider, // Called on release (and during hold via onLocalChange)
    (finalTempo) => {
      changeTempo({ value: finalTempo, goal: false });
    },
    {
      min: min_tempo_value,
      max: max_tempo_value,
      onLocalChange: (value) => changeTempo({ value, goal: false }), // Update slider/percentage during hold
    },
  );

  const scrollBodyRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const [showScrollMoreHint, setShowScrollMoreHint] = useState(false);
  const [hasScrollOverflow, setHasScrollOverflow] = useState(false);
  const [thumbHeight, setThumbHeight] = useState(0);
  const [thumbTop, setThumbTop] = useState(0);
  const isDraggingThumb = useRef(false);
  const dragStartY = useRef(0);
  const dragStartScrollTop = useRef(0);

  const updateScrollState = useCallback(() => {
    const el = scrollBodyRef.current;
    if (!el) return;
    const { scrollHeight, clientHeight, scrollTop } = el;
    const epsilon = 2;
    const overflow = scrollHeight > clientHeight + epsilon;
    const atBottom = scrollTop + clientHeight >= scrollHeight - epsilon;
    setShowScrollMoreHint(overflow && !atBottom);
    setHasScrollOverflow(overflow);
    if (overflow) {
      const minThumb = 30;
      const newThumbHeight = Math.max((clientHeight / scrollHeight) * clientHeight, minThumb);
      const maxScroll = scrollHeight - clientHeight;
      const trackHeight = clientHeight - newThumbHeight;
      setThumbHeight(newThumbHeight);
      setThumbTop(maxScroll > 0 ? (scrollTop / maxScroll) * trackHeight : 0);
    }
  }, []);

  useLayoutEffect(() => {
    const el = scrollBodyRef.current;
    const inner = scrollContentRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(() => updateScrollState());
    ro.observe(el);
    if (inner) ro.observe(inner);
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  const handleThumbPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollBodyRef.current;
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    isDraggingThumb.current = true;
    dragStartY.current = e.clientY;
    dragStartScrollTop.current = el.scrollTop;
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const handleThumbPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingThumb.current) return;
      const el = scrollBodyRef.current;
      if (!el) return;
      e.preventDefault();
      const { scrollHeight, clientHeight } = el;
      const trackHeight = clientHeight - thumbHeight;
      if (trackHeight <= 0) return;
      const maxScroll = scrollHeight - clientHeight;
      const deltaY = e.clientY - dragStartY.current;
      el.scrollTop = dragStartScrollTop.current + (deltaY / trackHeight) * maxScroll;
    },
    [thumbHeight],
  );

  const handleThumbPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingThumb.current) return;
    isDraggingThumb.current = false;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }, []);

  return (
    <Box
      id="rep-tempo-container"
      sx={{
        position: 'absolute',
        zIndex: 1500,
        ...modalPosition,
        minWidth: isMobile ? '350px' : '350px',
        width: isMobile ? '370px' : '400px',
        borderRadius: '8px',
        boxShadow:
          '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
        px: '16px',
        pt: '16px',
        pb: '24px',
        backgroundColor: '#050B13',
        animation: 'fadeInSlideDown 0.2s ease-out',
        '@keyframes fadeInSlideDown': {
          '0%': {
            opacity: 0,
            transform: 'translateY(-10px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
      }}
      ref={tempoContainerRef}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-10px', // Half of the carot size to move it above the modal
          // Desktop: fixed offset under the toggle. Mobile: measured so it keeps
          // pointing at the toggle after the popup is clamped to the viewport.
          left: isMobile ? `${mobileAnchor?.caretLeft ?? 40}px` : '40px',
          width: '0',
          height: '0',
          borderLeft: '16px solid transparent',
          borderRight: '16px solid transparent',
          borderBottom: '16px solid #050B13', // Carot color should match modal background
        }}
      />

      <Box sx={{ position: 'relative' }}>
        <Box
          ref={scrollBodyRef}
          sx={{
            maxHeight: 'calc(100vh - var(--header-height) - 55px)',
            overflowY: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            ...detailsTitleTextStyles,
          }}
        >
          <Box ref={scrollContentRef}>
            <Box
              sx={{
                textAlign: 'center',
                marginTop: '6px',
                marginBottom: '16px',
                fontSize: '20px',
                position: 'relative',
              }}
            >
              TEMPO
            </Box>

            <Box
              sx={{
                textAlign: 'center',
                marginTop: '30px',
                marginBottom: '16px',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
              }}
            >
              <Box
                sx={{
                  display: 'inline-block',
                  '&:hover': { cursor: 'pointer' },
                  userSelect: 'none',
                  WebkitTouchCallout: 'none',
                  touchAction: 'manipulation',
                }}
                {...decrementHandlers}
              >
                <img
                  style={{ maxHeight: '28px', verticalAlign: 'top', pointerEvents: 'none' }}
                  src={TempoMinus}
                  draggable={false}
                />
              </Box>
              <Box
                sx={{ margin: '0px 20px', display: 'flex', fontSize: '24px', alignItems: 'start' }}
              >
                <Box sx={{ marginTop: '-3px', marginRight: '1px' }}>
                  {/* {tempo} */}
                  <TextField
                    inputRef={tempoInputRef}
                    type="number"
                    // value={tempo}
                    value={inputBoxText}
                    onChange={(e) => setInputBoxText(e.target.value)}
                    onBlur={handleInputBoxBlur}
                    inputProps={{
                      // min: 0,//10,
                      // max: 9999999,//300,
                      style: {
                        textAlign: 'left',
                        color: '#FFFFFF',
                        fontFamily: 'Lato',
                        fontWeight: 400,
                        fontSize: '24px',
                        lineHeight: '24px',
                        letterSpacing: '1px',
                        height: '0px',
                        margin: 0,
                        paddingLeft: '3px',
                        paddingRight: '3px',
                      },
                    }}
                    sx={{
                      width: '80px',
                      textAlign: 'center',
                      '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                        {
                          '-webkit-appearance': 'none',
                          margin: 0,
                        },
                      '& input[type=number]': {
                        '-moz-appearance': 'textfield',
                      },
                      maxWidth: '54px',
                      marginLeft: 0,
                      padding: 0,
                    }}
                  />
                </Box>{' '}
                <Box sx={{ fontSize: '16px' }}>BPM</Box>
              </Box>
              <Box
                sx={{
                  display: 'inline-block',
                  '&:hover': { cursor: 'pointer' },
                  userSelect: 'none',
                  WebkitTouchCallout: 'none',
                  touchAction: 'manipulation',
                }}
                {...incrementHandlers}
              >
                <img
                  style={{ maxHeight: '28px', verticalAlign: 'top', pointerEvents: 'none' }}
                  src={TempoPlus}
                  draggable={false}
                />
              </Box>
            </Box>
            {!freePlayMode && (
              <Box
                sx={{
                  textAlign: 'center',
                  marginTop: '32px',
                  marginBottom: '8px',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {tempo == (performanceTempo ?? 72) ? (
                  <Box
                    sx={{ textAlign: 'center', display: 'flex', alignItems: 'center', gap: '7px' }}
                  >
                    <Box
                      style={{
                        width: '15px',
                        height: '15px',
                        borderRadius: '50%',
                        backgroundColor: '#40E16D',
                      }}
                    ></Box>{' '}
                    At Goal Tempo
                  </Box>
                ) : tempo < (performanceTempo ?? 72) ? (
                  <Box
                    sx={{ textAlign: 'center', display: 'flex', alignItems: 'center', gap: '7px' }}
                  >
                    <Box
                      style={{
                        width: '15px',
                        height: '15px',
                        borderRadius: '50%',
                        backgroundColor: '#EB3459',
                      }}
                    ></Box>{' '}
                    Below Goal Tempo
                  </Box>
                ) : (
                  <Box
                    sx={{ textAlign: 'center', display: 'flex', alignItems: 'center', gap: '7px' }}
                  >
                    <Typography style={{ marginLeft: '8px', marginRight: '5px' }}> 🔥 </Typography>
                    Above Goal Tempo
                  </Box>
                )}
              </Box>
            )}

            <Box
              sx={{
                textAlign: 'center',
                marginTop: '6px',
                marginBottom: '16px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/*<StyledSlider
            handleSliderChange={handleSliderChange}
            performanceTempo={performanceTempo ?? 0}
            sliderValue={sliderValue}
            setSliderValue={setSliderValue}
            tempPctAdjustment={tempPctAdjustment}
            setTempPctAdjustment={setTempPctAdjustment}
          />*/}
              {useFeatureFlag(FeatureFlag.USE_ADAPTIVE_TEMPO) &&
              freePlayMode &&
              usingCustomGoalTempo &&
              ataEnabled ? (
                <StyledSliderVariant
                  variant="range"
                  sliderValueTempo={sliderValueTempo}
                  sliderValueGoal={sliderValueGoal}
                  onRangeChange={(left, right) => {
                    /*const tempoValue = Math.round(
                  sliderToTempo(left / 100, min_tempo_value, performanceTempo, max_tempo_value),
                );*/
                    changeTempo({ value: left / 100, goal: false, isSliderValue: true });
                    changeTempo({ value: right / 100, goal: true, isSliderValue: true });
                    //setSliderValueGoal(right);
                    //setSliderValueTempo(left);
                  }}
                  min={0}
                  max={100}
                  tooltipLeft="Current tempo"
                  tooltipRight="Goal tempo"
                  flipLabelPositionLeft={true}
                  labelLeft={Math.round(tempo)}
                  labelRight={Math.round(goalTempo)}
                />
              ) : (
                <StyledSliderVariant
                  variant="single"
                  value={sliderValueTempo}
                  onChange={(value) => {
                    /*const tempoValue = sliderToTempo(
                  value / 100,
                  min_tempo_value,
                  performanceTempo,
                  max_tempo_value,
                );
                //changeTempoManually(Math.round(tempoValue));
                setSliderValueTempo(value);*/
                    changeTempo({ value: value / 100, goal: false, isSliderValue: true });
                  }}
                  min={0}
                  max={100}
                  label=""
                  valueLabelFormat={(value) =>
                    `${Math.round(sliderToTempoPercentage(value / 100, min_tempo_value, performanceTempo, max_tempo_value))}%`
                  }
                />
              )}
            </Box>

            <Box
              sx={{
                textAlign: 'center',
                marginTop: '30px',
                marginBottom: '16px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0px',
              }}
            >
              <Box
                sx={{ magin: 0, padding: 0, '&:hover': { cursor: 'pointer' } }}
                onClick={resetTemp}
                id="rep-reset-button"
              >
                <img style={{ width: '32px' }} src={Reset} />
              </Box>
              Reset
            </Box>
            {!isRepertoire && <AutoTempoAdjustmentSettings />}
          </Box>
        </Box>
        {hasScrollOverflow && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: 0,
              right: '2px',
              width: '14px',
              height: '100%',
              zIndex: 2,
              touchAction: 'none',
            }}
          >
            <Box
              onPointerDown={handleThumbPointerDown}
              onPointerMove={handleThumbPointerMove}
              onPointerUp={handleThumbPointerUp}
              onPointerCancel={handleThumbPointerUp}
              sx={{
                position: 'absolute',
                top: `${thumbTop}px`,
                left: '3px',
                width: '8px',
                height: `${thumbHeight}px`,
                backgroundColor: 'rgba(255, 255, 255, 0.45)',
                borderRadius: '4px',
                cursor: 'grab',
                touchAction: 'none',
                transition: 'background-color 0.15s ease',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.6)' },
                '&:active': {
                  cursor: 'grabbing',
                  backgroundColor: 'rgba(255, 255, 255, 0.75)',
                },
              }}
            />
          </Box>
        )}
      </Box>
      {showScrollMoreHint && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: '5px',
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
            lineHeight: 0,
          }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: 22, color: 'rgba(255, 255, 255, 0.52)' }} />
        </Box>
      )}
    </Box>
  );
};

const AutoTempoAdjustmentSettings = () => {
  const { ataEnabled, usingCustomGoalTempo, freePlayMode } = useTempoContext();
  const [isAtaInfoShown, setIsInfoShown] = useState(false);
  const [isCustomGoalInfoShown, setIsCustomGoalInfoShown] = useState(false);
  // Local state: advanced threshold controls are hidden behind the slider
  // icon next to the ATA toggle. Because the whole popup unmounts when
  // closed, this resets automatically on each open.
  const [showAdvanced, setShowAdvanced] = useState(false);

  const dispatch = useDispatch();

  const handleAutoTempoAdjustmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(appActions.setAutoTempoAdjustment(e.target.checked));
    //setAtaEnabled(e.target.checked);
  };

  const handleUsingCustomGoalTempoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(appActions.setUsingCustomGoalTempo(e.target.checked));
    //setUsingCustomGoalTempo(e.target.checked);
  };

  const handleCustomGoalInfoButtonClick = () => {
    setIsCustomGoalInfoShown(!isCustomGoalInfoShown);
  };

  const handleAtaInfoButtonClick = () => {
    setIsInfoShown(!isAtaInfoShown);
  };

  if (!useFeatureFlag(FeatureFlag.USE_ADAPTIVE_TEMPO)) {
    return null;
  }
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginLeft: '15px',
          marginRight: '15px',
          padding: '5px',
          borderTop: '1px solid #333333',
        }}
      >
        <CustomSwitch
          checked={ataEnabled}
          onChange={handleAutoTempoAdjustmentChange}
          id="ata-switch"
        />
        <Typography sx={{ flex: 1 }}>Auto Tempo Adjustment</Typography>
        <Box
          onClick={() => setShowAdvanced((v) => !v)}
          aria-label="Toggle advanced ATA settings"
          role="button"
          sx={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            // Inline SVG with fill=currentColor so we can drive the color
            // via this Box's `color` prop. The project's Vite SVGR plugin
            // makes URL imports unreliable for CSS mask-image, so inlining
            // avoids the whole question.
            color: showAdvanced ? '#6F87F1' : 'white',
          }}
        >
          <svg width="16" height="19" viewBox="0 0 14 17" fill="none">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10.3453 15.5136C10.3453 15.666 10.4058 15.8121 10.5136 15.9199C10.6213 16.0276 10.7674 16.0882 10.9199 16.0882C11.0723 16.0882 11.2184 16.0276 11.3262 15.9199C11.4339 15.8121 11.4945 15.666 11.4945 15.5136V7.98666C12.1439 7.85472 12.7278 7.50239 13.1471 6.98932C13.5666 6.47618 13.7957 5.83392 13.7957 5.17118C13.7957 4.50852 13.5666 3.86618 13.1471 3.35311C12.7278 2.83998 12.1439 2.48764 11.4945 2.35577V0.574573C11.4945 0.422185 11.4339 0.27604 11.3262 0.16829C11.2184 0.0605324 11.0723 0 10.9199 0C10.7674 0 10.6213 0.0605324 10.5136 0.16829C10.4058 0.27604 10.3453 0.422185 10.3453 0.574573V2.35577C9.69583 2.48764 9.11196 2.83998 8.6926 3.35311C8.27317 3.86618 8.04406 4.50852 8.04406 5.17118C8.04406 5.83392 8.27317 6.47618 8.6926 6.98932C9.11196 7.50239 9.69583 7.85472 10.3453 7.98666V15.5136ZM9.19615 5.17118C9.19615 5.62835 9.37776 6.06682 9.70099 6.39005C10.0243 6.71335 10.4627 6.89497 10.9199 6.89497C11.377 6.89497 11.8154 6.71335 12.1387 6.39005C12.462 6.06682 12.6436 5.62835 12.6436 5.17118C12.6436 4.714 12.462 4.27561 12.1387 3.95231C11.8154 3.62908 11.377 3.44747 10.9199 3.44747C10.4627 3.44747 10.0243 3.62908 9.70099 3.95231C9.37776 4.27561 9.19615 4.714 9.19615 5.17118ZM2.30119 0.574573C2.30119 0.422185 2.36173 0.27604 2.46946 0.16829C2.5772 0.0605324 2.72341 0 2.87575 0C3.02817 0 3.1743 0.0605324 3.28204 0.16829C3.38978 0.27604 3.45035 0.422185 3.45035 0.574573V8.10158C4.09978 8.23344 4.68365 8.58577 5.10309 9.09884C5.52244 9.61198 5.75156 10.2543 5.75156 10.917C5.75156 11.5796 5.52244 12.222 5.10309 12.7351C4.68365 13.2482 4.09978 13.6005 3.45035 13.7324V15.5136C3.45035 15.666 3.38978 15.8121 3.28204 15.9199C3.1743 16.0276 3.02817 16.0882 2.87575 16.0882C2.72341 16.0882 2.5772 16.0276 2.46946 15.9199C2.36173 15.8121 2.30119 15.666 2.30119 15.5136V13.7324C1.65175 13.6005 1.06787 13.2482 0.648492 12.7351C0.229105 12.222 0 11.5796 0 10.917C0 10.2543 0.229105 9.61198 0.648492 9.09884C1.06787 8.58577 1.65175 8.23344 2.30119 8.10158V0.574573ZM1.15203 10.917C1.15203 10.4598 1.33364 10.0213 1.6569 9.69811C1.98017 9.37489 2.4186 9.19327 2.87575 9.19327C3.33293 9.19327 3.77139 9.37489 4.09462 9.69811C4.41793 10.0213 4.59954 10.4598 4.59954 10.917C4.59954 11.3742 4.41793 11.8125 4.09462 12.1359C3.77139 12.4591 3.33293 12.6407 2.87575 12.6407C2.4186 12.6407 1.98017 12.4591 1.6569 12.1359C1.33364 11.8125 1.15203 11.3742 1.15203 10.917Z"
              fill="currentColor"
            />
          </svg>
        </Box>
        <InfoOutline
          onClick={handleAtaInfoButtonClick}
          sx={{ color: isAtaInfoShown ? '#6F87F1' : 'white' }}
        />
      </Box>
      {isAtaInfoShown && (
        <Box
          sx={{
            backgroundColor: 'rgba(50, 53, 62, 1)',
            padding: '15px',
            borderRadius: '8px',
          }}
        >
          <Typography>
            Auto Tempo Adjustment automatically slows down and accelerates your tempo based on your
            performance.
          </Typography>
        </Box>
      )}
      {showAdvanced && <CustomSettings />}
      {ataEnabled && freePlayMode && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginLeft: '15px',
            marginRight: '15px',
            padding: '5px',
          }}
        >
          <CustomSwitchOutline
            checked={usingCustomGoalTempo}
            onChange={handleUsingCustomGoalTempoChange}
            id="custom-goal-tempo-switch"
          />
          <Typography sx={{ flex: 1 }}>Customize Goal Tempo</Typography>
          <InfoOutline
            onClick={handleCustomGoalInfoButtonClick}
            sx={{ color: isCustomGoalInfoShown ? '#6F87F1' : 'white' }}
          />
        </Box>
      )}
      {isCustomGoalInfoShown && (
        <Box
          sx={{
            backgroundColor: 'rgba(50, 53, 62, 1)',
            padding: '15px',
            borderRadius: '8px',
          }}
        >
          <Typography>In Free Play, you can set whatever goal tempo you’d like.</Typography>
        </Box>
      )}
    </Box>
  );
};

const detailsTitleTextStyles = {
  color: '#FFFFFF',
  fontFamily: 'Lato',
  fontSize: '15px',
  fontHeight: '700',
  lineHeight: '24px',
  letterSpacing: '1px',
  textAlign: 'center',
};

export default TempoControls;
